import { supabase } from "@/integrations/supabase/client";

export interface EmailTemplate {
  name: string;
  subject: string;
  html: string;
  variables?: Record<string, any>;
}

export interface SendEmailRequest {
  to: string | string[];
  subject: string;
  html?: string;
  text?: string;
  template_name?: string;
  template_variables?: Record<string, any>;
  from?: string;
  reply_to?: string;
  user_id?: string;
  campaign_id?: string;
}

export interface EmailStats {
  total_sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  spam: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
}

export class EmailService {
  static async sendEmail(request: SendEmailRequest): Promise<{
    success: boolean;
    results: Array<{
      email: string;
      success: boolean;
      error?: string;
      message_id?: string;
    }>;
  }> {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: request
    });

    if (error) {
      throw new Error(error.message);
    }

    return data;
  }

  static async sendTestEmail(to: string, subject: string = 'Test Email from TradeBot365') {
    return this.sendEmail({
      to,
      subject,
      html: `
        <h2>Test Email</h2>
        <p>This is a test email from TradeBot365 email system.</p>
        <p>If you received this, the email service is working correctly!</p>
        <p>Sent at: ${new Date().toLocaleString()}</p>
      `,
      text: `Test Email\n\nThis is a test email from TradeBot365 email system.\nIf you received this, the email service is working correctly!\n\nSent at: ${new Date().toLocaleString()}`
    });
  }

  static async sendSystemNotification(
    to: string | string[], 
    subject: string, 
    content: string,
    isHtml: boolean = true
  ) {
    return this.sendEmail({
      to,
      subject: `[TradeBot365] ${subject}`,
      [isHtml ? 'html' : 'text']: content
      // Let edge function use app_settings defaults for 'from'
    });
  }

  static async sendMarketingEmail(
    to: string | string[], 
    subject: string, 
    content: string,
    campaignId?: string
  ) {
    return this.sendEmail({
      to,
      subject,
      html: content,
      campaign_id: campaignId
      // Let edge function use app_settings defaults for 'from'
    });
  }

  static async getEmailMessages(limit: number = 50) {
    const { data, error } = await supabase
      .from('email_messages')
      .select(`
        id,
        to_email,
        from_email,
        subject,
        status,
        template_name,
        sent_at,
        delivered_at,
        opened_at,
        clicked_at,
        bounced_at,
        spam_at,
        error_message,
        created_at
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  }

  static async getEmailStats(dateFrom?: string, dateTo?: string): Promise<EmailStats> {
    let query = supabase
      .from('email_messages')
      .select('status, opened_at, clicked_at, bounced_at, spam_at, delivered_at');

    if (dateFrom) {
      query = query.gte('created_at', dateFrom);
    }
    if (dateTo) {
      query = query.lte('created_at', dateTo);
    }

    const { data, error } = await query;
    if (error) throw error;

    const stats: EmailStats = {
      total_sent: data.length,
      delivered: data.filter(msg => msg.delivered_at).length,
      opened: data.filter(msg => msg.opened_at).length,
      clicked: data.filter(msg => msg.clicked_at).length,
      bounced: data.filter(msg => msg.bounced_at).length,
      spam: data.filter(msg => msg.spam_at).length,
      delivery_rate: 0,
      open_rate: 0,
      click_rate: 0
    };

    if (stats.total_sent > 0) {
      stats.delivery_rate = (stats.delivered / stats.total_sent) * 100;
      stats.open_rate = (stats.opened / stats.total_sent) * 100;
      stats.click_rate = (stats.clicked / stats.total_sent) * 100;
    }

    return stats;
  }

  static async getUnsubscribes() {
    const { data, error } = await supabase
      .from('email_unsubscribes')
      .select('email, reason, unsubscribed_at')
      .order('unsubscribed_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  static async getEmailEvents(messageId: string) {
    const { data, error } = await supabase
      .from('email_events')
      .select('event_type, timestamp, data')
      .eq('message_id', messageId)
      .order('timestamp', { ascending: true });

    if (error) throw error;
    return data;
  }

  // Predefined email templates
  static readonly TEMPLATES = {
    SYSTEM_MAINTENANCE: {
      name: 'system_maintenance',
      subject: 'Platform Maintenance Update - TradeBot365',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <div style="text-align: center; margin-bottom: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
            <img src="{{email_logo_url}}" alt="TradeBot365 Logo" style="max-height: 60px; width: auto;" />
          </div>
          
          <h2 style="color: #2563eb;">Platform Maintenance Notification</h2>
          <p>We will be performing scheduled maintenance to improve your trading experience.</p>
          
          <div style="background: #f8fafc; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <p><strong>Maintenance Schedule:</strong> {{maintenance_start}} to {{maintenance_end}}</p>
            <p><strong>Expected Impact:</strong> {{impact_description}}</p>
          </div>
          
          <p>This maintenance will enhance platform performance and add new features for better trading results.</p>
          <p>We appreciate your understanding and patience during this improvement process.</p>
          
          <p>Best regards,<br>
          <strong>TradeBot365 Technical Team</strong><br>
          <small>support@tradebot365.com</small></p>
        </div>
      `
    },
    WELCOME: {
      name: 'welcome',
      subject: 'Your TradeBot365 Account is Ready',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <div style="text-align: center; margin-bottom: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
            <img src="{{email_logo_url}}" alt="TradeBot365 Logo" style="max-height: 60px; width: auto;" />
          </div>
          
          <h2 style="color: #16a34a;">Welcome to TradeBot365, {{name}}!</h2>
          
          <p>Your trading account has been successfully activated. You now have access to our advanced trading tools and analytics.</p>
          
          <div style="background: #f0f9ff; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <h3 style="margin-top: 0;">Next Steps:</h3>
            <ul style="margin-bottom: 0;">
              <li>Complete your trading preferences setup</li>
              <li>Explore our educational resources</li>
              <li>Connect with our support team if needed</li>
            </ul>
          </div>
          
          <p>Our platform is designed to help you make informed trading decisions with data-driven insights.</p>
          
          <p>Success in trading,<br>
          <strong>TradeBot365 Team</strong><br>
          <small>support@tradebot365.com</small></p>
        </div>
      `
    },
    PLATFORM_UPDATE: {
      name: 'platform_update',
      subject: 'Platform Enhancement Available',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <div style="text-align: center; margin-bottom: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
            <img src="{{email_logo_url}}" alt="TradeBot365 Logo" style="max-height: 60px; width: auto;" />
          </div>
          
          <h2 style="color: #2563eb;">{{update_title}}</h2>
          
          <p>We've enhanced your trading platform with new capabilities designed to improve your results.</p>
          
          <div style="background: #f8fafc; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <h3 style="margin-top: 0;">What's New:</h3>
            <p>{{update_description}}</p>
            <p><strong>Key Benefits:</strong> {{update_benefits}}</p>
          </div>
          
          <p>These improvements are available now in your trading dashboard.</p>
          
          <p>Happy trading,<br>
          <strong>TradeBot365 Development Team</strong><br>
          <small>support@tradebot365.com</small></p>
        </div>
      `
    }
  };
}